import React, { useMemo } from "react";
import styles from "./reports.module.scss";
import Input from "components/Input";
import Table from "components/Table";
import { employeeDocsColumns } from "./columns";
import { useQuery } from "@tanstack/react-query";
import { getEmployeeTaxDocs, downloadEmployeeTaxDoc } from "services/reports";

const mapDoc = (doc) => ({
    id: doc.id,
    employee: doc.employeeName || doc.employee || "—",
    label: doc.label || doc.documentType || doc.name || "—",
    jurisdiction: doc.jurisdiction || "—",
    category: doc.category || doc.documentType || "—",
    year: doc.year || (doc.filedOn ? new Date(doc.filedOn).getFullYear() : "—"),
    field_on: doc.filedOn
        ? new Date(doc.filedOn).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "short", day: "numeric" })
        : doc.createdAt
        ? new Date(doc.createdAt).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "short", day: "numeric" })
        : "—",
});

const selectStyle = {
    border: '1px solid #dee2e6',
    borderRadius: 8,
    padding: '8px 32px 8px 12px',
    fontSize: 13,
    color: '#333',
    background: '#fff',
    cursor: 'pointer',
    appearance: 'auto',
    minWidth: 130,
    height: 40,
};

const EmployeeTaxDocs = () => {
    const [search, setSearch] = React.useState("");
    const [yearFilter, setYearFilter] = React.useState("");

    const { data: rawDocs, isLoading, isError } = useQuery({
        queryKey: ["employeeTaxDocs"],
        queryFn: getEmployeeTaxDocs,
    });

    const columns = useMemo(() => employeeDocsColumns(downloadEmployeeTaxDoc), []);

    const docs = Array.isArray(rawDocs) ? rawDocs : (rawDocs?.data ?? rawDocs?.items ?? []);
    const mapped = useMemo(() => docs.map(mapDoc), [docs]);

    // Build year options from actual documents
    const availableYears = useMemo(() => {
        const years = [...new Set(mapped.map(d => d.year).filter(y => y && y !== "—"))].sort((a, b) => b - a);
        return years;
    }, [mapped]);

    const tableData = useMemo(() => {
        return mapped.filter((d) => {
            const matchesSearch =
                !search ||
                d.employee.toLowerCase().includes(search.toLowerCase()) ||
                d.label.toLowerCase().includes(search.toLowerCase()) ||
                d.jurisdiction.toLowerCase().includes(search.toLowerCase());
            const matchesYear = !yearFilter || String(d.year) === yearFilter;
            return matchesSearch && matchesYear;
        });
    }, [mapped, search, yearFilter]);

    const hasFilter = search || yearFilter;

    return (
        <div className={styles.empTaxDocsContainer}>
            <div className={styles.header}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Year dropdown */}
                    <select
                        value={yearFilter}
                        onChange={(e) => setYearFilter(e.target.value)}
                        style={selectStyle}
                    >
                        <option value="">All Years</option>
                        {availableYears.map(y => (
                            <option key={y} value={String(y)}>{y}</option>
                        ))}
                    </select>

                    {/* Clear filters */}
                    {hasFilter && (
                        <button
                            onClick={() => { setYearFilter(''); setSearch(''); }}
                            style={{ fontSize: 12, color: '#1B75F0', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '0 4px' }}
                        >
                            ✕ Clear
                        </button>
                    )}
                </div>
                <Input
                    placeholder="Search by Name, Label, or Jurisdiction"
                    style={{ minWidth: "300px" }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>
            {isLoading ? (
                <p className="text-center mt-4 text-muted">Loading documents...</p>
            ) : isError ? (
                <p className="text-center mt-4 text-danger">Failed to load employee tax documents.</p>
            ) : tableData.length === 0 ? (
                <p className="text-center mt-4 text-muted">{hasFilter ? 'No documents match your filters.' : 'No employee tax documents found.'}</p>
            ) : (
                <Table
                    headers={columns}
                    data={tableData}
                    containerStyle={{ border: "none", borderRadius: "0px" }}
                />
            )}
        </div>
    );
};

export default EmployeeTaxDocs;
